import * as api from '../../api';
import { Map, List } from 'immutable';
import { errorUtilities } from '../../common';
import * as intakeFormConstants from './intake-form-constants';

export const fetchFormActions = api.fetchActions('modify-intake', 'fetch-intake-form');
export const addFormActions = api.fetchActions('modify-intake', 'add-form');
export const updateFormActions = api.fetchActions('modify-intake', 'update-form');
export const fetchTouchpointInfoActions = api.fetchActions('modify-intake', 'fetch-touchpoint-info');
export const fetchTouchpointActions = api.fetchActions('modify-intake', 'fetch-touchpoint');
export const fetchValidTouchpointsActions = api.fetchActions('modify-intake', 'fetch-valid-touchpoints');
export const fetchProgramDemographicsActions = api.fetchActions('modify-intake', 'fetch-program-demographics');
export const fetchCurrentFormPreviewActions = api.fetchActions('modify-intake', 'fetch-current-form-preview');
export const zipAutofillActions = api.fetchActions('modify-intake', 'zip-autofill');

export const actionTypes = {
  ...fetchFormActions.actionTypes,
  ...addFormActions.actionTypes,
  ...updateFormActions.actionTypes,
  ...fetchTouchpointInfoActions.actionTypes,
  ...fetchTouchpointActions.actionTypes,
  ...fetchValidTouchpointsActions.actionTypes,
  ...fetchProgramDemographicsActions.actionTypes,
  ...fetchCurrentFormPreviewActions.actionTypes,
  ...zipAutofillActions.actionTypes,
  UPDATE_PREVIEW_STATE : 'modify-intake.update-preview-state',
  CLEAR_PREVIEW_ANSWERS : 'modify-intake.clear-preview-answers',
  PRESERVE_EDITS : 'modify-intake.preserve-edits',
  UPDATE_VALUE : 'modify-intake.update-value',
  SELECT_ADD_TOUCHPOINT : 'modify-intake.select-add-touchpoint',
  SELECT_EDIT_TOUCHPOINT : 'modify-intake.select-edit-touchpoint',
  UPDATE_VALIDATION : 'modify-intake.update-validation',
  SHOW_ADD_VALIDATION : 'modify-intake.show-add-validation',
  SHOW_EDIT_VALIDATION : 'modify-intake.show-edit-validation',
  CLEAR_ANSWERS : 'modify-intake.clear-answers',
  SHOW_MODAL : 'modify-intake.show-modal',
  UPDATE_PREVIEW_TAB_REDIRECT: 'modify-intake.update-preview-tab-redirect',
  SHOW_DEMO_QUESTION_EDIT_MODAL_EDIT_INTAKE: 'modify-intake.show-demo-question-edit-modal-edit-intake',
  SHOW_DEMO_QUESTION_EDIT_MODAL_CREATE_INTAKE: 'modify-intake.show-demo-question-edit-modal-create-intake',
  SHOW_TOUCHPOINT_QUESTION_EDIT_MODAL_EDIT_INTAKE: 'modify-intake.show-touchpoint-question-edit-modal-edit-intake',
  SHOW_TOUCHPOINT_QUESTION_EDIT_MODAL_CREATE_INTAKE: 'modify-intake.show-touchpoint-question-edit-modal-create-intake',
  CLOSE_DEMO_QUESTION_EDIT_MODAL: 'modify-intake.close-demo-question-edit-modal',
  CLOSE_TOUCHPOINT_QUESTION_EDIT_MODAL: 'modify-intake.close-touchpoint-question-edit-modal',
  UPDATE_CUSTOM_LABEL: 'modify-intake.update-custom-label',
  UPDATE_CUSTOM_TOOLTIP: 'modify-intake.update-custom-tooltip',
  SET_SELECTED_SAVE_OPTION : 'modify-intake.set-selected-save-option',
}

export const fetchForm =(id) => api.shouldExecute(['modifyIntake', 'intakeForms', id],
  api.getThunk(`/intake/${id}`, {}, fetchFormActions, {id}));

export const fetchTouchpointInfo = () => api.shouldExecute(['modifyIntake', 'touchpointInfo'],
  api.getThunk(`/intake/GetAllFormsInfo`, {}, fetchTouchpointInfoActions));

export const fetchTouchpoint = (siteId, programId, touchpointId) => api.shouldExecute(['modifyIntake', 'touchpoints', touchpointId],
  api.getThunk(`/sites/${siteId}/programs/${programId}/touchpoint-forms/${touchpointId}`, {}, fetchTouchpointActions, {touchpointId}));

export const fetchValidTouchpoints = (siteId, programId) => api.shouldExecute(['modifyIntake', 'validIntakeTouchpoints', siteId, programId],
  api.getThunk(`/sites/${siteId}/programs/${programId}/touchpoint-forms/GetFormsWithAddPerms`, {}, fetchValidTouchpointsActions, {siteId, programId}));

export const fetchProgramDemographics = (siteId, programId) =>
  api.getThunk(`/demographics/get-intake-demographics`, {siteId: siteId, programId: programId}, fetchProgramDemographicsActions, {siteId, programId});

export const zipAutofill = (siteId, programId, elementId, zip, country) =>
  api.getThunk(`/touchpoint-responses/zip-autofill`, {siteId, programId, zip, country}, zipAutofillActions, {siteId, programId, elementId});

/**
 * Fetch a preview of an intake form currently being edited or created
 * @param {string} formId guid. only applicable if form had previously been created. else, should be null
 * @param {string} siteId guid representing site this intake form belongs to
 * @param {string} programId guid representing program this intake form belongs to
 * @param {*} value contains fields from the 'about' tab, list of demographics questions, and list of form questions
 */
export function fetchCurrentFormPreview(formId, siteId, programId, value) {
  return (dispatch) => {
    const returnValue = {
      siteId,
      programId,
      ...value.toObject()
    }
    if (!!formId && formId !== intakeFormConstants.createNewIntakeId)
      return dispatch(api.postThunk(`/intake/preview/${formId}`, returnValue, fetchCurrentFormPreviewActions));
    else
      return dispatch(api.postThunk(`/intake/preview`, returnValue, fetchCurrentFormPreviewActions));
  }
}

export function addForm(siteId, programId) {
  return (dispatch, getState) => {
    const state = getState();
    const { modifyIntake } = state;
    const { newValue } = modifyIntake;
    const returnValue = {
      siteId,
      programId,
      ...newValue.toObject()
    }
    return dispatch(api.postThunk(`/intake`, returnValue, addFormActions));
  }
}

/**
 * Save updates made to a pre-existing intake form
 * @param {string} id guid of the intake form being updated
 */
export function updateForm(id) {
  return (dispatch, getState) => {
    const state = getState();
    const { modifyIntake } = state;
    const { editValues } = modifyIntake;
    const value = editValues && editValues.get(id);
    return dispatch(api.putThunk(`/intake/${id}`, value, updateFormActions, { id }));
  }
}

export function updateOrder(orderedListPath, uniqueIdName, uniqueId, moveUp) {
  return (dispatch, getState) => {
    const state = getState();
    const { modifyIntake } = state;
    const list = modifyIntake && modifyIntake.getIn(orderedListPath);
    const sortedList = !!list ? list.sort((a, b) =>
      a.get(intakeFormConstants.order) - b.get(intakeFormConstants.order)) : List([]);
    const sortedIndex = sortedList.findIndex(val => val.get(uniqueIdName) === uniqueId);
    if(sortedIndex === -1 || (sortedIndex === 0 && moveUp) || (sortedIndex === sortedList.size - 1 && !moveUp))
      return;
    else {
      const question = sortedList.get(sortedIndex);
      const adjacentQuestion = moveUp ? sortedList.get(sortedIndex - 1) : sortedList.get(sortedIndex + 1);
      const adjacentId = adjacentQuestion.get(uniqueIdName);

      const index = list.findIndex(val => val.get(uniqueIdName) === uniqueId);
      const path = [...orderedListPath, index, intakeFormConstants.order];
      dispatch(updateValue(path, adjacentQuestion.get(intakeFormConstants.order)))

      const adjacentIndex = list.findIndex(val => val.get(uniqueIdName) === adjacentId);
      const adjacentPath = [...orderedListPath, adjacentIndex, intakeFormConstants.order];
      return dispatch(updateValue(adjacentPath, question.get(intakeFormConstants.order)))
    }
  }
}

export function populateTouchpointAddValues() {
  return (dispatch, getState) => {
    const { modifyIntake } = getState();
    const { touchpoints, selectedAddTouchpoint } = modifyIntake || {};
    const touchpointState = touchpoints && touchpoints.get(selectedAddTouchpoint);
    const { data : selectedTouchpoint } = touchpointState;
    const elements = selectedTouchpoint && selectedTouchpoint.elements;

    var value = List([]);
    elements && elements.forEach( (element, index) => {
      if(!element.isUnsupported && !element.disabled && element.type !== "ignore" && element.type !== "unknown") {
        value = value.push(Map({
          touchpointFormId: selectedAddTouchpoint,
          elementId: element.id,
          isIncluded: element.isRequired,
          isRequired: element.isRequired,
          order : index
        }));
      }
    });

    return dispatch(updateValue(['newValue', intakeFormConstants.touchpointSection], value));
  }
}

/**
 * Updates the 'newValue' for intake demographics by copying the program demographic questions to the intake form
 * @param {string} siteId guid representing site in which the demographics are associated
 * @param {string} programId guid representing the program in which the demographics are associated
 */
export function populateDemographicsAddValues(siteId, programId) {
  return (dispatch, getState) => {
    const { modifyIntake } = getState();
    const { programDemographics : allProgramsDemographics } = modifyIntake || {};
    const programDemographicsState = allProgramsDemographics && allProgramsDemographics.getIn([siteId, programId]);
    const { data : demographicsQuestions } = programDemographicsState || {};
    const elements = demographicsQuestions && demographicsQuestions.elements;

    var value = List([]);
    elements && elements.forEach( (demographic, index) => {
      if(!demographic.isUnsupported && !demographic.disabled) {
        value = value.push(Map({
          demographicId: demographic.id,
          dataType: (typeof demographic.type === "string" && demographic.type.length > 1) ?
            demographic.type.charAt(0).toUpperCase() + demographic.type.substr(1) : demographic.type,
          isIncluded: demographic.isRequired,
          isRequired: demographic.isRequired,
          order : index
        }));
      }
    });

    return dispatch(updateValue(['newValue', intakeFormConstants.demographicsSection], value));
  }
}

export function selectAddTouchpoint(touchpointId) {
  return {
    type : actionTypes.SELECT_ADD_TOUCHPOINT,
    touchpointId
  }
}

export function populateTouchpointEditValues(id) {
  return (dispatch, getState) => {
    const { modifyIntake } = getState();
    const { touchpoints, intakeForms, selectedEditTouchpoint } = modifyIntake || {};
    const touchpointId = selectedEditTouchpoint && selectedEditTouchpoint.get(id);
    const touchpointState = touchpoints && touchpoints.get(touchpointId);
    const { data : selectedTouchpoint } = touchpointState;
    const elements = selectedTouchpoint && selectedTouchpoint.elements;
    const intakeFormState = intakeForms && intakeForms.get(id);
    const intakeForm = intakeFormState && intakeFormState.data;
    const touchpointQuestions = intakeForm && intakeForm.touchpointQuestions;

    var value = List([]);
    elements && elements.forEach( (element, index) => {
      const touchpointQuestion = touchpointQuestions && touchpointQuestions.find(val => val.elementId === element.id);
      if(!element.isUnsupported && !element.disabled && element.type !== "ignore" && element.type !== "unknown") {
        value = value.push(Map({
          touchpointFormId: touchpointId,
          elementId: element.id,
          isIncluded: element.isRequired ?
            element.isRequired :
            !!touchpointQuestion ? touchpointQuestion.isIncluded : false,
          isRequired: element.isRequired ?
            element.isRequired :
            !!touchpointQuestion ? touchpointQuestion.isRequired : false,
          order : !!touchpointQuestion ?
            touchpointQuestion.order :
            !!touchpointQuestions ? touchpointQuestions.length + index : index,
          label: touchpointQuestion && touchpointQuestion.label,
          tooltip: touchpointQuestion && touchpointQuestion.tooltip,
        }))
      }
    });

    return dispatch(updateValue(['editValues', id, intakeFormConstants.touchpointSection], value));
  }
}

/**
 * Updates the 'editValues' for the intake form demographics, using what already exists in the intake form
 * or setting a default for that demographic question
 * @param {string} intakeFormId guid associated with the form being edited
 * @param {string} siteId guid representing site in which the demographics are associated
 * @param {string} programId guid representing program in which the demographics are associated
 */
export function populateDemographicsEditValues(intakeFormId, siteId, programId) {
  return (dispatch, getState) => {
    const { modifyIntake } = getState();
    const { programDemographics : allProgramsDemographics, intakeForms } = modifyIntake || {};
    const programDemographicsState = allProgramsDemographics && allProgramsDemographics.getIn([siteId, programId]);
    const { data : programDemographicsQuestions } = programDemographicsState;
    const elements = programDemographicsQuestions && programDemographicsQuestions.elements;
    const intakeFormState = intakeForms && intakeForms.get(intakeFormId);
    const intakeForm = intakeFormState && intakeFormState.data;
    const intakeDemographicQuestions = intakeForm && intakeForm.demographicQuestions;

    var value = List([]);
    elements && elements.forEach( (demographic, index) => {
      const demographicQuestion = intakeDemographicQuestions && intakeDemographicQuestions.find(val => val.demographicId === demographic.id);
      if(!demographic.isUnsupported && !demographic.disabled) {
        value = value.push(Map({
          demographicId: demographic.id,
          dataType: (typeof demographic.type === "string" && demographic.type.length > 1) ?
            demographic.type.charAt(0).toUpperCase() + demographic.type.substr(1) : demographic.type,
          isIncluded: demographic.isRequired ?
            demographic.isRequired :
            !!demographicQuestion ? demographicQuestion.isIncluded : false,
          isRequired: demographic.isRequired ?
            demographic.isRequired :
            !!demographicQuestion ? demographicQuestion.isRequired : false,
          order : !!demographicQuestion ?
            demographicQuestion.order :
            !!demographicQuestion ? demographicQuestion.length + index : index,
          label: demographicQuestion && demographicQuestion.label,
          tooltip: demographicQuestion && demographicQuestion.tooltip,
        }));
      }
    });
    return dispatch(updateValue(['editValues', intakeFormId, intakeFormConstants.demographicsSection], value));
  }
}

export function selectEditTouchpoint(id, touchpointId) {
  return {
    type : actionTypes.SELECT_EDIT_TOUCHPOINT,
    id,
    touchpointId
  }
}

export function updateValue(path, value) {
  return {
    type : actionTypes.UPDATE_VALUE,
    path,
    value
  }
}

/**
 * updates the state of the intake form preview
 * @param {string} siteId guid for site associated with the intake form
 * @param {string} programId guid for program associated with the intake form
 * @param {boolean} disableReCaptcha should recaptcha be shown in preview
 * @param {boolean} inProgress is the form in the process of being edited/created?
 * @param {*} value the form value
 */
export function updatePreviewState(siteId, programId, disableReCaptcha, inProgress, value) {
  return {
    type : actionTypes.UPDATE_PREVIEW_STATE,
    siteId,
    programId,
    disableReCaptcha,
    inProgress,
    value
  }
}

export function clearPreviewState() {
  return (dispatch) => {
    dispatch(updatePreviewState(null, null, false, false, null));
  }
}

/**
 * Copies state of a modified intake form (being created or edited) to the intake form preview state
 * @param {*} formId (if editing an existing form) the id associated with the intake form
 * @param {*} siteId id of the site associated with this intake form
 * @param {*} programId id of the program associated with this intake
 * @param {*} updateType what type of update is being made to the form. see intakeFormConstants.editStatus
 */
export function updateModifiedPreviewState(formId, siteId, programId, disableReCaptcha, updateType) {
  return (dispatch, getState) => {
    const { modifyIntake } = getState();
    const { newValue, editValues } = modifyIntake || {};
    var value;
    switch(updateType){
      case intakeFormConstants.editStatus.editExisting:
        value = editValues.get(formId);
        break;
      case intakeFormConstants.editStatus.createNew:
      default:
        value = newValue;
        break;
    }
    dispatch(updatePreviewState(siteId, programId, disableReCaptcha, true, value))
    return Promise.resolve(value);
  }
}

export function validateForm(newDataPath, oldDataPath, validationPath) {
  return (dispatch, getState) => {
    const state = getState();
    const { modifyIntake } = state;
    var newData = newDataPath && modifyIntake.getIn(newDataPath);
    var oldData = oldDataPath && Map(modifyIntake.getIn(oldDataPath));
    var errors = [];

    var newName = newData && newData.get(intakeFormConstants.nameSection);
    var oldName = oldData && oldData.get(intakeFormConstants.nameSection);
    if(newName === "" || (!newName && !oldName)) {
      errors.push(errorUtilities.validationError(intakeFormConstants.nameSection,
        'Form Name', 'modify-intake-form', errors.length, "Form Name is required"));
    }

    var newTitle = newData && newData.get(intakeFormConstants.titleSection);
    var oldTitle = oldData && oldData.get(intakeFormConstants.titleSection);
    if(newTitle === "" || (!newTitle && !oldTitle)) {
      errors.push(errorUtilities.validationError(intakeFormConstants.titleSection,
        'Title', 'modify-intake-form', errors.length, "Title is required"));
    }

    return dispatch(setValidationResult(validationPath, errors));
  }
}

function setValidationResult(path, value) {
  return {
    type : actionTypes.UPDATE_VALIDATION,
    path,
    value
  }
}

export function showAddValidation() {
  return {
    type : actionTypes.SHOW_ADD_VALIDATION
  }
}

export function showEditValidation(id) {
  return {
    type : actionTypes.SHOW_EDIT_VALIDATION,
    id
  }
}

export function clearAnswers() {
  return {
    type : actionTypes.CLEAR_ANSWERS,
  }
}

export function showModal(id, show) {
  return {
    type : actionTypes.SHOW_MODAL,
    id,
    show
  }
}

export function showDemoQuestionEditModalEditIntake(demographicId, intakeFormId) {
  return {
    type : actionTypes.SHOW_DEMO_QUESTION_EDIT_MODAL_EDIT_INTAKE,
    demographicId,
    intakeFormId,
  }
}

export function showDemoQuestionEditModalCreateIntake(demographicId) {
  return {
    type : actionTypes.SHOW_DEMO_QUESTION_EDIT_MODAL_CREATE_INTAKE,
    demographicId,
  }
}

export function showTouchpointQuestionEditModalEditIntake(touchpointId, intakeFormId) {
  return {
    type : actionTypes.SHOW_TOUCHPOINT_QUESTION_EDIT_MODAL_EDIT_INTAKE,
    touchpointId,
    intakeFormId,
  }
}

export function showTouchpointQuestionEditModalCreateIntake(touchpointId) {
  return {
    type : actionTypes.SHOW_TOUCHPOINT_QUESTION_EDIT_MODAL_CREATE_INTAKE,
    touchpointId,
  }
}

export function closeDemoQuestionEditModal() {
  return {
    type : actionTypes.CLOSE_DEMO_QUESTION_EDIT_MODAL,
  }
}

export function closeTouchpointQuestionEditModal() {
  return {
    type : actionTypes.CLOSE_TOUCHPOINT_QUESTION_EDIT_MODAL,
  }
}

/**
 * Set or reset flag to determine if ongoing edits to an intake form should be saved
 * @param {boolean} preserve whether or not to preserve the working edits of an intake form
 */
export function preserveEdits(preserve) {
  return{
    type: actionTypes.PRESERVE_EDITS,
    preserve
  }
}

/**
 * save the particular tab you are on within the edit/create intake form
 * @param {string} tab ending of the url to direct the preview intake form back to the edit screen tab
 * on which the user left off. for example, '/demographics'
 */
export function updatePreviewTabRedirect(tab){
  return{
    type: actionTypes.UPDATE_PREVIEW_TAB_REDIRECT,
    tab
  }
}
/**
 * save the duplicate check options modal window 
 * @param {string} id the id of the current intake form to be modified
 * @param {string} dupeCheckNotificationType value of 'specific' or 'generic' to be set
 */
export function updateDupeCheckNotification(id, dupeCheckNotificationType) {
  return (dispatch, getState) => {
    if (id === undefined) {
      // this is  a new intake form
      return dispatch(updateValue(['newValue', 'dupeCheckNotificationType'], dupeCheckNotificationType));
    }
    return dispatch(updateValue(['editValues', id, 'dupeCheckNotificationType'], dupeCheckNotificationType));
  }
}

/**
 * update the duplicate check options modal window state with the current selection
 * @param {string} value of 'specific' or 'generic' 
 */
export function setSelectedSaveOption(value) {
  return {
    type: actionTypes.SET_SELECTED_SAVE_OPTION,
    value
  }
}

export function updateCustomLabel(label) {
  return {
    type: actionTypes.UPDATE_CUSTOM_LABEL,
    label
  }
}

export function updateCustomTooltip(tooltip) {
  return {
    type: actionTypes.UPDATE_CUSTOM_TOOLTIP,
    tooltip
  }
}
